import React, { useState, useEffect } from 'react';
import axios from 'axios';
import API_URL from '../config';
import { formatMoney } from '../utils/formatMoney';

const SheetsManagement = () => {
  const [activeTab, setActiveTab] = useState('upload'); // 'upload' or 'view'
  const [file, setFile] = useState(null);
  const [year, setYear] = useState(new Date().getFullYear());
  const [month, setMonth] = useState(new Date().getMonth() + 1);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  // Sheets view state
  const [sheets, setSheets] = useState([]);
  const [sheetsLoading, setSheetsLoading] = useState(false);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [editingSheet, setEditingSheet] = useState(null);
  const [editFile, setEditFile] = useState(null);
  const [editYear, setEditYear] = useState('');
  const [editMonth, setEditMonth] = useState('');
  const [editLoading, setEditLoading] = useState(false);

  useEffect(() => {
    if (activeTab === 'view') {
      fetchSheets();
    }
  }, [activeTab]);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file) {
      setError('Please select a file');
      return;
    }

    if (!year || !month) {
      setError('Please select year and month');
      return;
    }

    setLoading(true);
    setError(null);
    setMessage(null);

    const formData = new FormData();
    formData.append('file', file);
    formData.append('year', year);
    formData.append('month', month);

    try {
      const response = await axios.post(`${API_URL}/api/ledger/upload`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      setMessage(`Successfully uploaded ${response.data.count} records! Sheet ID: ${response.data.sheetId}`);
      setFile(null);
      document.getElementById('file-input').value = '';

      // Refresh sheets list
      if (activeTab === 'view') {
        fetchSheets();
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to upload file');
    } finally {
      setLoading(false);
    }
  };

  const fetchSheets = async () => {
    setSheetsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API_URL}/api/sheets`);
      setSheets(response.data.sheets);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch sheets');
    } finally {
      setSheetsLoading(false);
    }
  };

  const handleViewSheet = async (sheetId) => {
    try {
      const response = await axios.get(`${API_URL}/api/sheets/${sheetId}`);
      setSelectedSheet(response.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to fetch sheet details');
    }
  };

  const handleEditSheet = (sheet) => {
    setEditingSheet(sheet);
    setEditYear(sheet.year.toString());
    setEditMonth(sheet.month.toString());
    setEditFile(null);
    setError(null);
  };

  const handleCancelEdit = () => {
    setEditingSheet(null);
    setEditFile(null);
    setEditYear('');
    setEditMonth('');
    setError(null);
  };

  const handleSaveEdit = async (e) => {
    e.preventDefault();
    if (!editFile) {
      setError('Please select a file to upload');
      return;
    }

    setEditLoading(true);
    setError(null);

    const formData = new FormData();
    formData.append('file', editFile);
    if (editYear) formData.append('year', editYear);
    if (editMonth) formData.append('month', editMonth);

    try {
      await axios.put(`${API_URL}/api/sheets/${editingSheet.id}`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });

      alert('Sheet updated successfully!');
      handleCancelEdit();
      fetchSheets();
      if (selectedSheet && selectedSheet.sheet.id === editingSheet.id) {
        handleViewSheet(editingSheet.id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to update sheet');
    } finally {
      setEditLoading(false);
    }
  };

  const handleDeleteSheet = async (sheetId, fileName) => {
    if (!window.confirm(`Are you sure you want to delete sheet "${fileName}"? This will also delete all associated ledger entries.`)) {
      return;
    }

    try {
      await axios.delete(`${API_URL}/api/sheets/${sheetId}`);
      alert('Sheet deleted successfully!');
      fetchSheets();
      if (selectedSheet && selectedSheet.sheet.id === sheetId) {
        setSelectedSheet(null);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete sheet');
    }
  };

  const handleDownloadSheet = async (sheetId, fileName) => {
    try {
      const response = await axios.get(`${API_URL}/api/sheets/${sheetId}/download`, {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to download sheet');
    }
  };

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 10 }, (_, i) => currentYear - 5 + i);

  return (
    <div>
      {/* Tab Navigation */}
      <div className="subtab-bar">
        <button
          type="button"
          className={`subtab ${activeTab === 'upload' ? 'active' : ''}`}
          onClick={() => setActiveTab('upload')}
        >
          Upload Sheet
        </button>
        <button
          type="button"
          className={`subtab ${activeTab === 'view' ? 'active' : ''}`}
          onClick={() => setActiveTab('view')}
        >
          View Sheets
        </button>
      </div>

      {error && (
        <div className="alert alert-error" style={{ marginBottom: '20px' }}>{error}</div>
      )}

      {message && (
        <div className="alert alert-success" style={{ marginBottom: '20px' }}>
          {message}
        </div>
      )}

      {/* Upload Tab */}
      {activeTab === 'upload' && (
        <div className="card">
          <h2 style={{ marginBottom: '20px' }}>Upload Excel Sheet</h2>

          <form onSubmit={handleSubmit}>
            <div className="form-group">
              <label htmlFor="file-input">Select Excel File</label>
              <div className="file-input-wrapper">
                <input
                  id="file-input"
                  type="file"
                  accept=".xlsx,.xls"
                  onChange={handleFileChange}
                  required
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '20px' }}>
              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="year">Year</label>
                <select
                  id="year"
                  value={year}
                  onChange={(e) => setYear(e.target.value)}
                  required
                >
                  {years.map((y) => (
                    <option key={y} value={y}>
                      {y}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group" style={{ flex: 1 }}>
                <label htmlFor="month">Month</label>
                <select
                  id="month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  required
                >
                  <option value="1">January</option>
                  <option value="2">February</option>
                  <option value="3">March</option>
                  <option value="4">April</option>
                  <option value="5">May</option>
                  <option value="6">June</option>
                  <option value="7">July</option>
                  <option value="8">August</option>
                  <option value="9">September</option>
                  <option value="10">October</option>
                  <option value="11">November</option>
                  <option value="12">December</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? 'Uploading...' : 'Upload & Seed Data'}
            </button>
          </form>
        </div>
      )}

      {/* View Sheets Tab */}
      {activeTab === 'view' && (
        <>
          <div className="card" style={{ marginBottom: '20px' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h2 style={{ margin: 0 }}>Uploaded Sheets ({sheets.length})</h2>
              <button
                onClick={fetchSheets}
                className="btn btn-primary"
                disabled={sheetsLoading}
              >
                {sheetsLoading ? 'Loading...' : 'Refresh'}
              </button>
            </div>
          </div>

          {sheetsLoading && <div className="loading">Loading sheets...</div>}

          {sheets.length === 0 && !sheetsLoading && (
            <div className="card">
              <p>No sheets uploaded yet. Upload a sheet from the Upload Sheet tab.</p>
            </div>
          )}

          {sheets.length > 0 && (
            <div className="card">
              <div className="table-scroll">
              <table className="table">
                <thead>
                  <tr>
                    <th>File Name</th>
                    <th>Year</th>
                    <th>Month</th>
                    <th>Entries</th>
                    <th>Uploaded</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sheets.map((sheet) => (
                    <tr key={sheet.id}>
                      <td style={{ fontWeight: '500' }}>{sheet.fileName}</td>
                      <td>{sheet.year}</td>
                      <td>
                        {new Date(sheet.year, sheet.month - 1).toLocaleString('default', { month: 'long' })}
                      </td>
                      <td>{sheet.entryCount || 0}</td>
                      <td>{new Date(sheet.createdAt).toLocaleDateString()}</td>
                      <td>
                        <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                          <button
                            type="button"
                            onClick={() => handleViewSheet(sheet.id)}
                            className="btn btn-primary btn-small"
                          >
                            View
                          </button>
                          <button
                            type="button"
                            onClick={() => handleEditSheet(sheet)}
                            className="btn btn-success btn-small"
                          >
                            Edit
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDownloadSheet(sheet.id, sheet.fileName)}
                            className="btn btn-neutral-outline btn-small"
                          >
                            Download
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteSheet(sheet.id, sheet.fileName)}
                            className="btn btn-danger btn-small"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              </div>
            </div>
          )}

          {/* Edit Modal */}
          {editingSheet && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div className="card" style={{
                maxWidth: '500px',
                width: '90%',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative'
              }}>
                <h2 style={{ marginBottom: '20px' }}>Edit Sheet: {editingSheet.fileName}</h2>

                <form onSubmit={handleSaveEdit}>
                  <div className="form-group">
                    <label htmlFor="edit-sheet-file">New Excel File *</label>
                    <input
                      id="edit-sheet-file"
                      type="file"
                      accept=".xlsx,.xls"
                      onChange={(e) => setEditFile(e.target.files[0])}
                      required
                    />
                  </div>

                  <div className="filter-group">
                    <div className="form-group">
                      <label htmlFor="edit-sheet-year">Year (Optional)</label>
                      <select
                        id="edit-sheet-year"
                        value={editYear}
                        onChange={(e) => setEditYear(e.target.value)}
                      >
                        <option value="">Keep existing</option>
                        {years.map((y) => (
                          <option key={y} value={y}>
                            {y}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div className="form-group">
                      <label htmlFor="edit-sheet-month">Month (Optional)</label>
                      <select
                        id="edit-sheet-month"
                        value={editMonth}
                        onChange={(e) => setEditMonth(e.target.value)}
                      >
                        <option value="">Keep existing</option>
                        <option value="1">January</option>
                        <option value="2">February</option>
                        <option value="3">March</option>
                        <option value="4">April</option>
                        <option value="5">May</option>
                        <option value="6">June</option>
                        <option value="7">July</option>
                        <option value="8">August</option>
                        <option value="9">September</option>
                        <option value="10">October</option>
                        <option value="11">November</option>
                        <option value="12">December</option>
                      </select>
                    </div>
                  </div>

                  <div style={{ display: 'flex', gap: '10px', marginTop: '20px' }}>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={editLoading}
                    >
                      {editLoading ? 'Updating...' : 'Update Sheet'}
                    </button>
                    <button
                      type="button"
                      onClick={handleCancelEdit}
                      className="btn btn-cancel"
                      disabled={editLoading}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}

          {/* Sheet Details Modal */}
          {selectedSheet && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000
            }}>
              <div className="card" style={{
                maxWidth: '90%',
                width: '1200px',
                maxHeight: '90vh',
                overflow: 'auto',
                position: 'relative'
              }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
                  <h2>Sheet Details: {selectedSheet.sheet.fileName}</h2>
                  <button
                    type="button"
                    onClick={() => setSelectedSheet(null)}
                    className="btn btn-cancel"
                  >
                    Close
                  </button>
                </div>

                <div className="summary" style={{ marginBottom: '20px' }}>
                  <div className="summary-card">
                    <h3>Year</h3>
                    <div className="value">{selectedSheet.sheet.year}</div>
                  </div>
                  <div className="summary-card">
                    <h3>Month</h3>
                    <div className="value">
                      {new Date(selectedSheet.sheet.year, selectedSheet.sheet.month - 1).toLocaleString('default', { month: 'long' })}
                    </div>
                  </div>
                  <div className="summary-card">
                    <h3>Total Entries</h3>
                    <div className="value">{selectedSheet.entryCount}</div>
                  </div>
                  <div className="summary-card">
                    <h3>Uploaded</h3>
                    <div className="value" style={{ fontSize: '14px' }}>
                      {new Date(selectedSheet.sheet.createdAt).toLocaleString()}
                    </div>
                  </div>
                </div>

                <div className="card">
                  <h3 style={{ marginBottom: '20px' }}>
                    Entries ({selectedSheet.entries.length})
                  </h3>
                  {selectedSheet.entries.length === 0 ? (
                    <p>No entries found</p>
                  ) : (
                    <div className="table-scroll table-scroll--max-400">
                      <table className="table">
                        <thead>
                          <tr>
                            <th>No</th>
                            <th>File No</th>
                            <th>Amount</th>
                            <th>Type</th>
                            <th>Reason for payment</th>
                            <th>Date</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedSheet.entries.map((entry) => (
                            <tr key={entry.id}>
                              <td>{entry.no || '-'}</td>
                              <td>{entry.fileNo || '-'}</td>
                              <td
                                className={
                                  entry.type === 'incoming'
                                    ? 'amount-positive'
                                    : 'amount-negative'
                                }
                              >
                                {formatMoney(entry.amount)}
                              </td>
                              <td>
                                <span
                                  className={
                                    entry.type === 'incoming'
                                      ? 'badge badge-incoming'
                                      : 'badge badge-outgoing'
                                  }
                                >
                                  {entry.type}
                                </span>
                              </td>
                              <td
                                className="text-muted"
                                style={{ fontSize: '12px', maxWidth: '220px', overflow: 'hidden', textOverflow: 'ellipsis' }}
                                title={entry.reasonForPayment || ''}
                              >
                                {entry.reasonForPayment || '-'}
                              </td>
                              <td>
                                {new Date(entry.createdAt).toLocaleDateString()}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default SheetsManagement;

